/**
 * Gear-value normalization shared between URL middleware, OAuth authorize
 * persist, and the searchTones query helper.
 *
 * Three distinct rules:
 *
 *   - canonicalizeFullRig(): URL/persist form. Replace `full-rig` with
 *     `amp-cab` and dedupe. Used wherever we want a tidy canonical form
 *     (browser URLs, oauth_request_extras, etc.).
 *
 *   - stripIrFromGears(): IR-Gear deprecation handler. Filter out any
 *     `ir` tokens; if `ir` was present, signal that the caller should
 *     infer `format=ir` (unless the caller already has an explicit
 *     format, in which case the explicit value wins).
 *
 *   - expandLegacyGears(): filter-time symmetric expansion. If either
 *     `full-rig` or `amp-cab` is in the list, ensure both are. Used by
 *     searchTones so a query for one returns rows of the other during
 *     the transition window (and forever, as the partner-API contract).
 *
 *   - isAcceptedGearInput(): input-validation allowlist. The catalog GEAR
 *     enum plus the two legacy aliases below, which are no longer enum
 *     members but are still accepted on every input surface.
 *
 *   - isCreatableGear(): write allowlist. Catalog gears only — the aliases
 *     are readable but never persistable.
 */

import { CREATABLE_GEARS, GEAR } from '@/utils/constants';

const FULL_RIG = 'full-rig';
const AMP_CAB = 'amp-cab';
const IR = 'ir';

/**
 * Gear values still accepted on input after being dropped from the GEAR enum.
 * They reach the normalizers above rather than a Gear filter, so they must
 * survive validation — see the API docs Deprecated Fields section.
 */
export const LEGACY_GEAR_ALIASES: readonly string[] = [FULL_RIG, IR];

/** Every gear value an input surface accepts: catalog gears + legacy aliases. */
export const ACCEPTED_GEAR_INPUTS: readonly string[] = [
  ...Object.values(GEAR),
  ...LEGACY_GEAR_ALIASES,
];

export function isAcceptedGearInput(value: string): boolean {
  return ACCEPTED_GEAR_INPUTS.includes(value);
}

/**
 * Gear values a write path may persist: the catalog gears only, no aliases.
 * Narrower than isAcceptedGearInput — a read surface accepts `full-rig` and
 * `ir` and normalizes them, but nothing may put them back in tones.gear.
 */
export function isCreatableGear(value: unknown): value is GEAR {
  return typeof value === 'string' && CREATABLE_GEARS.includes(value as GEAR);
}

export function canonicalizeFullRig(gears: string[] | null): {
  gears: string[] | null;
  changed: boolean;
} {
  if (!gears || !gears.includes(FULL_RIG)) return { gears, changed: false };
  const out = new Set(gears);
  out.delete(FULL_RIG);
  out.add(AMP_CAB);
  return { gears: Array.from(out), changed: true };
}

export function stripIrFromGears(gears: string[] | null): {
  gears: string[] | null;
  shouldInferIrFormat: boolean;
} {
  if (!gears || !gears.includes(IR)) return { gears, shouldInferIrFormat: false };
  const filtered = gears.filter((g) => g !== IR);
  return {
    gears: filtered.length ? filtered : null,
    shouldInferIrFormat: true,
  };
}

/**
 * Resolve a feed endpoint's singular `gear=` param into the filters to apply.
 *
 * `ir` is still accepted on input but is no longer a Gear, so filtering
 * `gear = 'ir'` matches nothing and returns a silent empty 200. It selects on
 * Format instead — the same move /api/v1/tones/search makes with
 * stripIrFromGears. Catalog gears keep the symmetric full-rig/amp-cab
 * expansion.
 */
export function feedGearFilter(gear: string | null | undefined): {
  gears: string[] | null;
  irFormat: boolean;
} {
  const { gears, shouldInferIrFormat } = stripIrFromGears(gear ? [gear] : null);
  return {
    gears: gears?.length ? expandLegacyGears(gears) ?? gears : null,
    irFormat: shouldInferIrFormat,
  };
}

export function expandLegacyGears(gears: string[] | null): string[] | null {
  if (!gears) return gears;
  if (!gears.includes(FULL_RIG) && !gears.includes(AMP_CAB)) return gears;
  const out = new Set(gears);
  out.add(FULL_RIG);
  out.add(AMP_CAB);
  return Array.from(out);
}
